/**
 * Une exigence, une vérité à l'écran — chunk 9.BK.1.
 *
 * ## Le défaut que ce module ferme
 *
 * Constaté en terrain : « Liste d'employés — Résolu » et « Liste d'employés — À
 * résoudre » côte à côte, pour **le même requis**, avec le même `field_path`.
 *
 * Ce n'est **pas** un défaut du modèle. `app.missing_information` conserve
 * délibérément l'historique : l'index partiel de 0068 n'interdit qu'**un seul
 * item vivant** par requis, et 9.AC.1 l'a voulu ainsi — « un requis satisfait
 * puis redevenu non satisfait doit pouvoir produire un nouvel item, sans effacer
 * la trace du précédent ». Un verrou posé puis retiré laisse donc une ligne
 * `fulfilled` et une ligne `open`, et c'est correct.
 *
 * Ce qui n'est pas correct, c'est de montrer **les deux** au conseiller. La
 * surface opérationnelle répond à « qu'est-ce qui manque **maintenant** » ;
 * l'historique appartient à l'audit.
 *
 * ## La règle
 *
 * Une ligne par requis : **la plus récente**. Elle porte toujours l'état
 * courant — un nouvel item n'est créé qu'après la clôture du précédent, si bien
 * que la dernière dit « résolu », « à résoudre » ou « écarté » selon le cas.
 *
 * Les items **sans requis** — saisis à la main pour une demande ponctuelle — ne
 * sont jamais regroupés : rien ne les rend redondants, et deux questions
 * distinctes peuvent légitimement se ressembler.
 */

export type LigneRegroupable = {
  readonly id: string;
  readonly requirement_id?: string | null;
  readonly created_at: string;
};

/**
 * Ne garde, par requis, que la ligne la plus récente. L'ordre d'origine est
 * préservé : la liste ne doit pas se réorganiser sous les yeux de quelqu'un.
 */
export function unItemParExigence<T extends LigneRegroupable>(lignes: readonly T[]): T[] {
  const derniere = new Map<string, T>();
  for (const l of lignes) {
    const cle = l.requirement_id;
    if (!cle) continue;
    const vue = derniere.get(cle);
    // `>=` : à horodatage égal, la dernière rencontrée gagne. Deux lignes créées
    // dans la même milliseconde viennent forcément de la même transaction, et
    // c'est la seconde qui décrit l'état d'arrivée.
    if (!vue || l.created_at >= vue.created_at) derniere.set(cle, l);
  }
  return lignes.filter((l) => !l.requirement_id || derniere.get(l.requirement_id)?.id === l.id);
}
